import * as React from "react";
import { QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { ClientService, DatabaseConnectionModel } from "@app/client";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { GlobalConstants } from "@app/shared/constants";
import { TooltipWrapper } from "@app/shared";
import { Features } from "@app/feature-flags";
import { QESDateInput } from "../../../shared/qes-standards/forms/DateInput";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ClientNetworkBenchmarkingDataRightsEnum } from "@app/client/client";
import { Roles, UserContext } from "@app/user";

interface ClientInfoProps {
  /**
   * Used to disable all fields. This is typically in the case of a deactivated client.
   */
  disabled?: boolean;
}

const clientService = new ClientService();
export const ClientInfo: React.FC<React.PropsWithChildren<ClientInfoProps>> = (props: ClientInfoProps) => {
  const [dbStringList, setDbStringList] = React.useState<DatabaseConnectionModel[]>([]);
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  const clientContext = React.useContext(ClientContext);
  const user = React.useContext(UserContext);
  const userIsInOperationAdmin = React.useRef(user.roles.includes(Roles.OPERATIONS_ADMIN)).current;

  React.useEffect(() => {
    clientService.getAllDatabaseConnections().then((dbc) => {
      setDbStringList(dbc);
    });
  }, []);

  const getContainerNameErrorText = (): string => {
    const value = clientContext.storageName;
    if (value.length < 3 || value.length > 63) {
      return "The Storage Container Name must be between 3 and 63 characters in length.";
    } else if (!value.match(/^[a-z]/)) {
      return "The Storage Container Name must start with a lowercase letter.";
    } else if (!value.match(/^[-a-z0-9]+$/)) {
      return "The Storage Container Name must only contain lowercase letters, numbers, and hyphens.";
    } else if (!!value.match(/(-{2,})|-$/)) {
      return "The Storage Container Name must not contain consecutive hyphens or end with a hyphen.";
    }
  }

  const containerNameValidation = React.useMemo(() => {
    return (
      <span>
        The Storage Container Name must:
        <ul className="text-left">
          <li>be between 3 and 63 characters in length</li>
          <li>start with a lowercase letter</li>
          <li>only contain lowercase letters, numbers, and hyphens</li>
          <li>not contain consecutive hyphens</li>
          <li>not end with a hyphen</li>
        </ul>
      </span>
    );
  }, []);

  const getOMActivationDateDisplayValue = (activationDate : string): string => {
    return activationDate && activationDate.length === 19
      ? activationDate.substring(0,10)
      : activationDate;
  }

  return (
    <>
      <QESTextInput
        containerClassName="col-8 mb-3"
        label="Name"
        testId="new-client__name"
        value={clientContext.clientName || ""}
        onChange={(e) => clientContext.setClientName(e.target.value)}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["clientName"]}
      />

      <QESTextInput
        containerClassName="col-8 mb-3"
        label="SalesForce Account ID"
        testId="new-client__sf-id"
        value={clientContext.sfId || ""}
        onChange={(e) => clientContext.setSfId(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["sfId"]}
      />

      <QESSelect
        containerClassName="col-12 mb-3"
        label="Database Connection"
        testId="new-client__db-string"
        value={clientContext.dbConnection}
        onChange={(e) => clientContext.setDbConnection(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["dbConnection"]}
      >
        <option key={0} value={0}>{"Select a database connection:"}</option>
        {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-12 mb-3"
        label="Cross-Project Database Connection"
        testId="new-client__cross-project-db-string"
        value={clientContext.crossProjectDbConnection}
        onChange={(e) => clientContext.setCrossProjectDbConnection(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["crossProjectDbConnection"]}
      >
        <option key={0} value={0}>{"Select a database connection:"}</option>
        {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      <QESSelect
          containerClassName="col-12 mb-3"
          label="Enterprise Compliance Dashboard Database Connection"
          testId="new-client__enterprise-compliance-dashboard-db-string"
          value={clientContext.enterpriseComplianceDashboardDbConnection}
          onChange={(e) => clientContext.setEnterpriseComplianceDashboardDbConnection(parseInt(e.target.value, 10))}
          disabled={props.disabled}
          required={true}
          isValid={clientContext.validationObject["enterpriseComplianceDashboardDbConnection"]}
        >
          <option key={0} value={0}>{"Select a database connection:"}</option>
          {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
      </QESSelect>

      { <QESSelect
          containerClassName="col-12 mb-3"
          label="PCI Dashboard Database Connection"
          testId="new-client__pci-dashboard-db-string"
          value={clientContext.pciDashboardDbConnection}
          onChange={(e) => clientContext.setPciDashboardDbConnection(parseInt(e.target.value, 10))}
          disabled={props.disabled}
          required={true}
          isValid={clientContext.validationObject["pciDashboardDbConnection"]}
        >
          <option key={0} value={0}>{"Select a database connection:"}</option>
          {dbStringList.map((s) => <option key={s.connectionId} value={s.connectionId}>{`${s.connectionDescription} (${s.connectionString})`}</option>)}
        </QESSelect>
      }
     
      <QESTextInput
        containerClassName="col-12 mb-3"
        label="Storage Connection String"
        testId="new-client__storage-string"
        value={clientContext.storageString || ""}
        onChange={(e) => clientContext.setStorageString(e.target.value)} disabled={props.disabled}
        required={true}
        isValid={clientContext.validationObject["storageString"]}
      />

      <QESTextInput
        containerClassName="col-8 mb-3"
        label="Outreach Client XID"
        testId="new-client__outreach-client-xid"
        value={clientContext.outreachClientXid || ""}
        onChange={(e) => clientContext.setOutreachClientXid(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["outreachClientXid"]}
      />

      <QESDateInput
        containerClassName="col-8 mb-3"
        label="Outreach Metrics Activation Date"
        testId="new-client__outreach-activation-date"
        value={getOMActivationDateDisplayValue(clientContext.outreachMetricsActivationDate)}
        onChange={(e) => clientContext.setOutreachMetricsActivationDate(e.target.value)}
        disabled={props.disabled}
        isValid={clientContext.validationObject["outreachClientActivationDate"]}
      />

      <QESTextInput
        containerClassName="col-8 mb-3"
        disabled={props.disabled}
        errorText={getContainerNameErrorText()}
        isValid={clientContext.validationObject["storageName"]}
        label="Storage Container Name"
        onChange={(e) => clientContext.setStorageName(e.target.value)}
        pattern="^(?=.{3,63}$)[a-z0-9]+(-[a-z0-9]+)*$"
        required={true}
        testId="new-client__storage-name"
        validationTooltipContent={containerNameValidation}
        value={clientContext.storageName || ""}
      />

      <QESTextInput
        containerClassName="col-9 mb-3" label="Logo URL" testId="new-client__logo-url" value={clientContext.logoUrl || ""} onChange={(e) => clientContext.setLogoUrl(e.target.value)} disabled={props.disabled} />

      <QESSelect
        containerClassName="col-8 mb-3"
        label="Volume Targeting Record Limit"
        testId="new-client__volume-targeting-record-limit"
        value={clientContext.bulkUploadRecordLimit}
        onChange={(e) => clientContext.setBulkUploadRecordLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["bulkUploadRecordLimit"]}
        >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 10000).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3"
        label="Volume Targeting Limit Hour Window"
        testId="new-client__volume-targeting-limit-hour-window"
        value={clientContext.bulkUploadLimitHourWindow}
        onChange={(e) => clientContext.setBulkUploadLimitHourWindow(parseInt(e.target.value, 10))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["bulkUploadLimitHourWindow"]}
        >
        {[8, 10, 12].map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3"
        label="Network Servicing Provider Report Record Limit"
        testId="new-client__network-provider-report-record-limit"
        value={clientContext.networkServicingProviderReportLimit}
        onChange={(e) => clientContext.setNetworkServicingProviderReportLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["networkServicingProviderReportLimit"]}
      >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 200000).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      <QESSelect
        containerClassName="col-8 mb-3"
        label="Prospect/Market Servicing Provider Report Record Limit"
        testId="new-client__prospect-market-provider-report-record-limit"
        value={clientContext.prospectMarketServicingProviderReportLimit}
        onChange={(e) => clientContext.setProspectMarketServicingProviderReportLimit(parseInt(e.target.value))}
        disabled={props.disabled}
        isValid={clientContext.validationObject["prospectMarketServicingProviderReportLimit"]}
      >
        {Array.from({ length: 10 }, (_, i) => (i + 1) * 200000).map((s, i) => <option key={i} value={s}>{s}</option>)}
      </QESSelect>

      {!!(featureFlags.has(Features.NETWORK_BENCHMARKING)) && (
        <QESSelect
          containerClassName="col-8 mb-3"
          label="Competitive Insights Data Rights"
          testId="new-client__network-benchmarking-data-rights"
          value={clientContext.networkBenchmarkingDataRights || ""}
          onChange={(x) => clientContext.setNetworkBenchmarkingDataRights(x.target.value as ClientNetworkBenchmarkingDataRightsEnum)}
          disabled={true}
        >
          {Object.values(ClientNetworkBenchmarkingDataRightsEnum).map((x, index) => <option key={index} value={x}>{x}</option>)}
        </QESSelect>
      )}

      {clientContext.clientId !== GlobalConstants.QuestClientId ? (
        <>
          <QESCheckbox containerClassName="col-12" label='Resource Admin Guest Logins Allowed' testId="new-client__resource_admin_guest_logins_allowed" onChange={(e) => clientContext.setResourceAdminGuestLoginsAllowed(e.target.checked)} checked={clientContext.resourceAdminGuestLoginsAllowed} />
          <QESCheckbox containerClassName="col-12 mb-4" label='Quest Viewer Guest Logins Allowed' testId="new-client__quest_viewer_guest_logins_allowed" onChange={(e) => clientContext.setQuestViewerGuestLoginsAllowed(e.target.checked)} checked={clientContext.questViewerGuestLoginsAllowed} />
        </>
      ) : null}

      <QESCheckbox containerClassName="col-12 mb-4" label='Quest Internal (allow internal-only roles)' testId="new-client__quest_internal" onChange={(e) => clientContext.setIsQuestInternal(e.target.checked)} checked={clientContext.isQuestInternal} />

      <TooltipWrapper text='When enabled, a confirmation message is displayed during user creation to remind the user that this client has special rules related to creation of new users.'>
        <QESCheckbox
          containerClassName="col-12 mb-4"
          label='Enable "Restricted" User Creation'
          testId="new-client__restricted_user_creation_enabled"
          onChange={(e) => clientContext.setRestrictedUserCreationEnabled(e.target.checked)}
          checked={clientContext.restrictedUserCreationEnabled}
        />
      </TooltipWrapper>

      {!!userIsInOperationAdmin && 
      <div>
        <TooltipWrapper text='These checkboxes have a major performance impact! Do not check these unless you know what you are doing!'>
          <QESCheckbox containerClassName="col-12" label='Disable Access Cache Reads' testId="new-client__disable_access_cache_reads" onChange={(e) => clientContext.setDisableAccessCacheReads(e.target.checked)} checked={clientContext.disableAccessCacheReads} />
          <QESCheckbox containerClassName="col-12 mb-4" label='Disable Access Cache Writes' testId="new-client__disable_access_cache_writes" onChange={(e) => clientContext.setDisableAccessCacheWrites(e.target.checked)} checked={clientContext.disableAccessCacheWrites} />
        </TooltipWrapper>
      </div>}
    </>
  );
}
